/**
 * ============================================================
 * TRONCA BOUTIQUE — SLIDE PRIMARIA V1
 * ============================================================
 *
 * Gestisce:
 * - cambio slide con frecce;
 * - indicatori inferiori;
 * - autoplay;
 * - pausa al passaggio del mouse;
 * - pausa quando la finestra non è visibile;
 * - swipe su smartphone e tablet;
 * - navigazione con tastiera;
 * - compatibilità con l’editor Shopify;
 * - rispetto delle preferenze di movimento ridotto.
 */

(() => {
  'use strict';

  const SLIDER_SELECTOR = '[data-tronca-primary-slider]';
  const REDUCED_MOTION_QUERY = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  /**
   * Inizializza tutti gli slider presenti nella pagina.
   */
  function initializeAllSliders() {
    document
      .querySelectorAll(SLIDER_SELECTOR)
      .forEach((slider) => {
        initializeSlider(slider);
      });
  }

  /**
   * Inizializza una singola istanza.
   *
   * @param {HTMLElement} slider
   */
  function initializeSlider(slider) {
    if (!(slider instanceof HTMLElement)) return;

    if (slider.dataset.troncaSliderInitialized === 'true') {
      return;
    }

    slider.dataset.troncaSliderInitialized = 'true';

    const slides = Array.from(
      slider.querySelectorAll('[data-tronca-primary-slide]')
    ).filter((element) => element instanceof HTMLElement);

    const dots = Array.from(
      slider.querySelectorAll('[data-tronca-primary-dot]')
    ).filter((element) => element instanceof HTMLButtonElement);

    const previousButton = slider.querySelector(
      '[data-tronca-primary-previous]'
    );

    const nextButton = slider.querySelector(
      '[data-tronca-primary-next]'
    );

    if (slides.length === 0) return;

    const autoplayEnabled =
      slider.dataset.autoplay === 'true';

    const pauseOnHover =
      slider.dataset.pauseOnHover === 'true';

    const autoplaySpeed = Math.max(
      3000,
      Number.parseInt(
        slider.dataset.autoplaySpeed || '6000',
        10
      ) || 6000
    );

    let currentIndex = Math.max(
      0,
      slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      )
    );

    let autoplayTimer = null;
    let interactionPaused = false;
    let documentPaused = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;
    let transitionLocked = false;
    let transitionTimer = null;

    /**
     * Restituisce un indice valido.
     *
     * @param {number} index
     * @returns {number}
     */
    function normalizeIndex(index) {
      if (index < 0) {
        return slides.length - 1;
      }

      if (index >= slides.length) {
        return 0;
      }

      return index;
    }

    /**
     * Aggiorna lo stato degli indicatori.
     *
     * @param {number} activeIndex
     */
    function updateDots(activeIndex) {
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute(
          'aria-selected',
          isActive ? 'true' : 'false'
        );

        dot.tabIndex = isActive ? 0 : -1;
      });
    }

    /**
     * Aggiorna gli attributi accessibili delle slide.
     *
     * @param {number} activeIndex
     */
    function updateSlideAccessibility(activeIndex) {
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;

        slide.setAttribute(
          'aria-hidden',
          isActive ? 'false' : 'true'
        );

        slide
          .querySelectorAll(
            'a, button, input, select, textarea, [tabindex]'
          )
          .forEach((element) => {
            if (!(element instanceof HTMLElement)) return;

            if (isActive) {
              const previousTabIndex =
                element.dataset.troncaPreviousTabindex;

              if (previousTabIndex !== undefined) {
                if (previousTabIndex === '') {
                  element.removeAttribute('tabindex');
                } else {
                  element.setAttribute(
                    'tabindex',
                    previousTabIndex
                  );
                }

                delete element.dataset.troncaPreviousTabindex;
              }
            } else {
              if (
                element.dataset.troncaPreviousTabindex ===
                undefined
              ) {
                element.dataset.troncaPreviousTabindex =
                  element.getAttribute('tabindex') || '';
              }

              element.setAttribute('tabindex', '-1');
            }
          });
      });
    }

    /**
     * Ferma il timer automatico.
     */
    function stopAutoplay() {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }

    /**
     * Verifica se l’autoplay può partire.
     *
     * @returns {boolean}
     */
    function canAutoplay() {
      return Boolean(
        autoplayEnabled &&
        slides.length > 1 &&
        !REDUCED_MOTION_QUERY.matches &&
        !interactionPaused &&
        !documentPaused
      );
    }

    /**
     * Avvia il prossimo cambio automatico.
     */
    function startAutoplay() {
      stopAutoplay();

      if (!canAutoplay()) return;

      autoplayTimer = window.setTimeout(() => {
        showSlide(currentIndex + 1, {
          restartAutoplay: true
        });
      }, autoplaySpeed);
    }

    /**
     * Mostra una slide.
     *
     * @param {number} requestedIndex
     * @param {{restartAutoplay?: boolean, focusDot?: boolean}} options
     */
    function showSlide(
      requestedIndex,
      {
        restartAutoplay = true,
        focusDot = false
      } = {}
    ) {
      if (slides.length <= 1) return;
      if (transitionLocked) return;

      const nextIndex = normalizeIndex(requestedIndex);

      if (nextIndex === currentIndex) {
        if (restartAutoplay) {
          startAutoplay();
        }

        return;
      }

      transitionLocked = true;

      const currentSlide = slides[currentIndex];
      const nextSlide = slides[nextIndex];

      window.clearTimeout(transitionTimer);

      currentSlide.classList.add('is-leaving');
      currentSlide.classList.remove('is-active');

      nextSlide.classList.remove('is-leaving');
      nextSlide.classList.add('is-active');

      currentIndex = nextIndex;

      updateDots(currentIndex);
      updateSlideAccessibility(currentIndex);

      if (
        focusDot &&
        dots[currentIndex] instanceof HTMLButtonElement
      ) {
        dots[currentIndex].focus();
      }

      transitionTimer = window.setTimeout(() => {
        slides.forEach((slide, index) => {
          if (index !== currentIndex) {
            slide.classList.remove('is-leaving');
          }
        });

        transitionLocked = false;
      }, 460);

      if (restartAutoplay) {
        startAutoplay();
      }
    }

    /**
     * Mostra la slide precedente.
     */
    function showPrevious() {
      showSlide(currentIndex - 1);
    }

    /**
     * Mostra la slide successiva.
     */
    function showNext() {
      showSlide(currentIndex + 1);
    }

    /**
     * Pausa temporaneamente l’autoplay.
     */
    function pauseForInteraction() {
      interactionPaused = true;
      stopAutoplay();
    }

    /**
     * Riprende l’autoplay.
     */
    function resumeAfterInteraction() {
      interactionPaused = false;
      startAutoplay();
    }

    /**
     * Gestione swipe.
     */
    function handleTouchEnd() {
      const horizontalDistance =
        touchEndX - touchStartX;

      const verticalDistance =
        touchEndY - touchStartY;

      const minimumSwipeDistance = 45;

      const isHorizontalSwipe =
        Math.abs(horizontalDistance) >
        Math.abs(verticalDistance);

      if (
        !isHorizontalSwipe ||
        Math.abs(horizontalDistance) <
          minimumSwipeDistance
      ) {
        return;
      }

      if (horizontalDistance < 0) {
        showNext();
      } else {
        showPrevious();
      }
    }

    /**
     * Freccia precedente.
     */
    previousButton?.addEventListener('click', (event) => {
      event.preventDefault();
      showPrevious();
    });

    /**
     * Freccia successiva.
     */
    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      showNext();
    });

    /**
     * Indicatori.
     */
    dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();

        const targetIndex = Number.parseInt(
          dot.dataset.slideIndex || '0',
          10
        );

        showSlide(targetIndex);
      });

      dot.addEventListener('keydown', (event) => {
        if (
          event.key !== 'ArrowLeft' &&
          event.key !== 'ArrowRight' &&
          event.key !== 'Home' &&
          event.key !== 'End'
        ) {
          return;
        }

        event.preventDefault();

        if (event.key === 'ArrowLeft') {
          showSlide(currentIndex - 1, {
            focusDot: true
          });
        }

        if (event.key === 'ArrowRight') {
          showSlide(currentIndex + 1, {
            focusDot: true
          });
        }

        if (event.key === 'Home') {
          showSlide(0, {
            focusDot: true
          });
        }

        if (event.key === 'End') {
          showSlide(slides.length - 1, {
            focusDot: true
          });
        }
      });
    });

    /**
     * Tastiera sulla sezione.
     */
    slider.addEventListener('keydown', (event) => {
      const target = event.target;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPrevious();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNext();
      }
    });

    /**
     * Pausa al passaggio del mouse.
     */
    if (pauseOnHover) {
      slider.addEventListener(
        'mouseenter',
        pauseForInteraction
      );

      slider.addEventListener(
        'mouseleave',
        resumeAfterInteraction
      );
    }

    /**
     * Pausa durante focus e interazione da tastiera.
     */
    slider.addEventListener('focusin', () => {
      pauseForInteraction();
    });

    slider.addEventListener('focusout', (event) => {
      const nextFocusedElement = event.relatedTarget;

      if (
        nextFocusedElement instanceof Node &&
        slider.contains(nextFocusedElement)
      ) {
        return;
      }

      resumeAfterInteraction();
    });

    /**
     * Swipe touch.
     */
    slider.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;

        pauseForInteraction();
      },
      {
        passive: true
      }
    );

    slider.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 1) return;

        touchEndX = event.touches[0].clientX;
        touchEndY = event.touches[0].clientY;
      },
      {
        passive: true
      }
    );

    slider.addEventListener(
      'touchend',
      () => {
        handleTouchEnd();

        window.setTimeout(() => {
          resumeAfterInteraction();
        }, 1200);
      },
      {
        passive: true
      }
    );

    slider.addEventListener(
      'touchcancel',
      () => {
        resumeAfterInteraction();
      },
      {
        passive: true
      }
    );

    /**
     * Pausa quando la pagina non è visibile.
     */
    document.addEventListener(
      'visibilitychange',
      () => {
        documentPaused = document.hidden;

        if (documentPaused) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      }
    );

    /**
     * Reagisce alla preferenza di movimento ridotto.
     */
    function handleReducedMotionChange() {
      if (REDUCED_MOTION_QUERY.matches) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    }

    if (
      typeof REDUCED_MOTION_QUERY.addEventListener ===
      'function'
    ) {
      REDUCED_MOTION_QUERY.addEventListener(
        'change',
        handleReducedMotionChange
      );
    } else {
      REDUCED_MOTION_QUERY.addListener(
        handleReducedMotionChange
      );
    }

    /**
     * Selezione di un blocco nell’editor Shopify.
     */
    slider.addEventListener(
      'shopify:block:select',
      (event) => {
        const selectedSlide =
          event.target instanceof HTMLElement
            ? event.target.closest(
                '[data-tronca-primary-slide]'
              )
            : null;

        if (!(selectedSlide instanceof HTMLElement)) {
          return;
        }

        pauseForInteraction();

        const selectedIndex = Number.parseInt(
          selectedSlide.dataset.slideIndex || '0',
          10
        );

        showSlide(selectedIndex, {
          restartAutoplay: false
        });
      }
    );

    /**
     * Deselezione del blocco nell’editor.
     */
    slider.addEventListener(
      'shopify:block:deselect',
      () => {
        resumeAfterInteraction();
      }
    );

    /**
     * Stato iniziale.
     */
    updateDots(currentIndex);
    updateSlideAccessibility(currentIndex);
    startAutoplay();

    /**
     * API pubblica della sezione.
     */
    slider.troncaPrimarySlider = {
      next: showNext,
      previous: showPrevious,
      goTo: showSlide,
      pause: pauseForInteraction,
      play: resumeAfterInteraction,
      getCurrentIndex: () => currentIndex
    };
  }

  /**
   * Avvio normale.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllSliders,
      {
        once: true
      }
    );
  } else {
    initializeAllSliders();
  }

  /**
   * Compatibilità con l’editor Shopify.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const section = event.target;

      if (!(section instanceof HTMLElement)) return;

      const slider = section.matches(SLIDER_SELECTOR)
        ? section
        : section.querySelector(SLIDER_SELECTOR);

      if (slider instanceof HTMLElement) {
        slider.dataset.troncaSliderInitialized = 'false';
        initializeSlider(slider);
      }
    }
  );

  /**
   * Ferma autoplay quando una sezione viene rimossa.
   */
  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section = event.target;

      if (!(section instanceof HTMLElement)) return;

      const slider = section.matches(SLIDER_SELECTOR)
        ? section
        : section.querySelector(SLIDER_SELECTOR);

      if (
        slider instanceof HTMLElement &&
        slider.troncaPrimarySlider
      ) {
        slider.troncaPrimarySlider.pause();
      }
    }
  );
})();